import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { MapPin, Building2, Clock, Users, RefreshCw, Coffee } from 'lucide-react';

interface Worker {
  id: string;
  name: string;
  role: string;
  status: string;
}

interface Site {
  id: string;
  name: string;
  address: string;
}

interface ActiveTimesheet {
  id: string;
  worker_id: string;
  site_id: string | null;
  archived_site_id?: string | null;
  clock_in_time: string;
  clock_out_time: string | null;
  clock_in_latitude: number | null;
  clock_in_longitude: number | null;
  site?: Site | null;
}

interface Break {
  id: string;
  worker_id: string;
  timesheet_id: string;
  end_time: string | null;
}

interface ActiveEmployee {
  worker: Worker;
  timesheet: ActiveTimesheet;
}

// Deterministic colour from name
const AVATAR_COLOURS = [
  'bg-orange-500', 'bg-blue-500', 'bg-emerald-500', 'bg-violet-500',
  'bg-rose-500', 'bg-amber-500', 'bg-teal-500', 'bg-cyan-500',
];

function avatarColour(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length];
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0].toUpperCase())
    .join('');
}

function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'w-12 h-12 text-base' : size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  return (
    <div className={`${sizeClass} ${avatarColour(name)} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}>
      {initials(name)}
    </div>
  );
}

function calcHours(clockIn: string): number {
  return (Date.now() - new Date(clockIn).getTime()) / 3_600_000;
}

function formatDuration(hours: number): string {
  if (hours < 0) return '0m';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function LiveTimer({ clockIn }: { clockIn: string }) {
  const [label, setLabel] = useState(() => formatDuration(calcHours(clockIn)));

  useEffect(() => {
    const tick = () => setLabel(formatDuration(calcHours(clockIn)));
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [clockIn]);

  return <span>{label}</span>;
}

export const LiveBoardPage: React.FC = () => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [activeTimesheets, setActiveTimesheets] = useState<ActiveTimesheet[]>([]);
  const [openBreaks, setOpenBreaks] = useState<Break[]>([]);
  const [archivedSiteMap, setArchivedSiteMap] = useState<Map<string, { name: string; address: string }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchData = useCallback(async () => {
    const [workersRes, timesheetsRes, breaksRes, archivedRes] = await Promise.all([
      supabase
        .from('workers')
        .select('id, name, role, status')
        .eq('status', 'active')
        .neq('role', 'Visitor')
        .order('name'),
      supabase
        .from('timesheets')
        .select('id, worker_id, site_id, archived_site_id, clock_in_time, clock_out_time, clock_in_latitude, clock_in_longitude, sites(id, name, address)')
        .eq('status', 'active')
        .is('clock_out_time', null)
        .order('clock_in_time', { ascending: true }),
      supabase
        .from('breaks')
        .select('id, worker_id, timesheet_id, end_time')
        .is('end_time', null),
      supabase
        .from('archived_sites')
        .select('original_site_id, site_data'),
    ]);

    // Build archived site map for orphaned timesheets
    const aMap = new Map<string, { name: string; address: string }>();
    (archivedRes.data || []).forEach((a: any) => {
      const sd = a.site_data;
      if (sd && a.original_site_id) {
        aMap.set(a.original_site_id, { name: sd.name || 'Archived Site', address: sd.address || '' });
      }
    });
    setArchivedSiteMap(aMap);

    if (workersRes.data) setWorkers(workersRes.data);
    if (timesheetsRes.data) {
      setActiveTimesheets(
        timesheetsRes.data.map((t: any) => {
          const site = Array.isArray(t.sites) ? t.sites[0] ?? null : t.sites ?? null;
          // If site is null but archived_site_id is set, synthesize from archived data
          if (!site && t.archived_site_id) {
            const archived = aMap.get(t.archived_site_id);
            if (archived) {
              return { ...t, site: { id: t.archived_site_id, name: archived.name, address: archived.address } } as ActiveTimesheet;
            }
          }
          return { ...t, site } as ActiveTimesheet;
        })
      );
    }
    if (breaksRes.data) setOpenBreaks(breaksRes.data);
    setLastUpdate(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();

    const channel = supabase
      .channel('live-board')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'timesheets' }, fetchData)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'breaks' }, fetchData)
      .subscribe();

    const poll = setInterval(fetchData, 30_000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, [fetchData]);

  const timesheetByWorker = Object.fromEntries(activeTimesheets.map(t => [t.worker_id, t]));
  const onBreakWorkerIds = new Set(openBreaks.map(b => b.worker_id));

  const active: ActiveEmployee[] = workers
    .filter(w => timesheetByWorker[w.id])
    .map(w => ({ worker: w, timesheet: timesheetByWorker[w.id] }));

  const inactive = workers.filter(w => !timesheetByWorker[w.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Board</h1>
          <p className="text-sm text-gray-500 mt-0.5">Real-time attendance — updates automatically</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-sm font-semibold text-gray-900">
              {active.length} of {workers.length} on site
            </span>
          </div>
          {inactive.length > 0 && (
            <span className="text-sm text-gray-400">{inactive.length} not clocked in</span>
          )}
          <span className="text-xs text-gray-400 border-l pl-4">
            Updated {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        </div>
      </div>

      {/* Currently On Site */}
      {active.length > 0 ? (
        <div>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Currently On Site
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {active.map(({ worker, timesheet }) => {
              const onBreak = onBreakWorkerIds.has(worker.id);
              return (
                <div
                  key={timesheet.id}
                  className={`bg-white rounded-2xl border shadow-sm overflow-hidden hover:shadow-md transition-all duration-200 ${
                    onBreak ? 'border-amber-100' : 'border-emerald-100'
                  }`}
                >
                  <div className={`h-1 bg-gradient-to-r ${onBreak ? 'from-amber-400 to-amber-500' : 'from-emerald-400 to-emerald-500'}`} />
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <Avatar name={worker.name} size="lg" />
                        <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 border-2 border-white rounded-full flex items-center justify-center ${
                          onBreak ? 'bg-amber-400' : 'bg-emerald-500'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full bg-white ${!onBreak ? 'animate-pulse' : ''}`} />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-bold text-gray-900 truncate">{worker.name}</p>
                        <p className="text-xs text-gray-500 truncate capitalize">
                          {worker.role.replace(/_/g, ' ')}
                        </p>
                        {onBreak ? (
                          <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-full">
                            <Coffee className="h-3 w-3 text-amber-500" />
                            <span className="text-xs font-medium text-amber-700">On Break</span>
                          </div>
                        ) : (
                          <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-xs font-medium text-emerald-700">On Site</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      {timesheet.site ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Building2 className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="font-medium truncate">{timesheet.site.name}</p>
                              {timesheet.archived_site_id && !timesheet.site_id && (
                                <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-semibold bg-gray-200 text-gray-600 border border-gray-300 flex-shrink-0">
                                  Archived
                                </span>
                              )}
                            </div>
                            {timesheet.site.address && (
                              <p className="text-xs text-gray-400 truncate">{timesheet.site.address}</p>
                            )}
                          </div>
                        </div>
                      ) : timesheet.clock_in_latitude ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-xs font-mono">
                            {Number(timesheet.clock_in_latitude).toFixed(4)},{' '}
                            {Number(timesheet.clock_in_longitude).toFixed(4)}
                          </span>
                        </div>
                      ) : null}

                      <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                        <div className="flex items-center gap-1.5 text-xs text-gray-400">
                          <Clock className="h-3 w-3" />
                          Clocked in {formatTime(timesheet.clock_in_time)}
                        </div>
                        <div className={`text-sm font-bold ${onBreak ? 'text-amber-500' : 'text-emerald-600'}`}>
                          <LiveTimer clockIn={timesheet.clock_in_time} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 text-center">
          <div className="w-14 h-14 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Users className="h-6 w-6 text-gray-300" />
          </div>
          <p className="text-gray-500 font-medium">No one is currently on site</p>
          <p className="text-sm text-gray-400 mt-1">Active employees will appear here in real time</p>
        </div>
      )}

      {/* Not Clocked In */}
      {inactive.length > 0 && (
        <div>
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
            Not Clocked In
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {inactive.map(worker => (
              <div
                key={worker.id}
                className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 opacity-50"
              >
                <div className="relative">
                  <Avatar name={worker.name} size="sm" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-gray-300 border-2 border-white rounded-full" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{worker.name}</p>
                  <p className="text-xs text-gray-400 truncate capitalize">{worker.role.replace(/_/g, ' ')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveBoardPage;
